import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import rentsService from "../services/rentsService";
import "./rentsLaravel.css";
import Loader from "../components/Loader";

const RentsLaravel = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  
  const [rents, setRents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortColumn, setSortColumn] = useState("unique_guardian_number");
  const [sortDirection, setSortDirection] = useState("asc");
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    ugd: true,
    guardian: true,
    guardianStatus: true,
    room: true,
    property: true,
    startDate: true,
    endDate: true,
    guardianOfficer: true,
    status: true,
    actions: true,
  });

  // Ref for search input to maintain focus
  const searchInputRef = useRef(null);
  const wasSearchFocusedRef = useRef(false);

  // Ref to track abort controller for cancelling in-flight requests
  const abortControllerRef = useRef(null);

  // Track when search input gains focus
  const handleSearchFocus = () => {
    wasSearchFocusedRef.current = true;
  };

  // Track when search input loses focus (but only if not due to rerender)
  const handleSearchBlur = () => {
    // Use setTimeout to check if focus moved to another element
    setTimeout(() => {
      if (document.activeElement !== searchInputRef.current) {
        wasSearchFocusedRef.current = false;
      }
    }, 0);
  };

  // Maintain focus on search input after rerenders
  useEffect(() => {
    if (wasSearchFocusedRef.current && searchInputRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
          searchInputRef.current.focus();
          // Restore cursor position if possible
          const input = searchInputRef.current;
          if (input.setSelectionRange && searchTerm.length > 0) {
            const cursorPos = Math.min(searchTerm.length, input.value.length);
            input.setSelectionRange(cursorPos, cursorPos);
          }
        }
      });
    }
  }, [rents, loading, searchTerm]);

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadRents = useCallback(async () => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setLoading(true);
      // Calculate start position for pagination
      const start = (currentPage - 1) * itemsPerPage;

      // Map sortColumn to column index for backend
      const columnMap = {
        unique_guardian_number: 0,
        guardianName: 1,
        guardianStatus: 2,
        roomName: 3,
        propertyName: 4,
        date_start: 5,
        date_end: 6,
        guardianOfficerName: 7,
        status: 8,
      };

      // Try to fetch from API first
      const response = await rentsService.getAllRents(
        {
          draw: currentPage,
          start: start,
          length: itemsPerPage,
          search: { value: debouncedSearchTerm },
          order: [{ column: columnMap[sortColumn] || 0, dir: sortDirection }],
        },
        abortController.signal
      );

      // Only update state if this request wasn't cancelled
      if (!abortController.signal.aborted) {
        setRents(response.data || []);
        setTotalRecords(response.recordsFiltered || response.recordsTotal || 0);
      }
    } catch (err) {
      // Ignore abort errors (they're expected when a new search is initiated)
      if (err.name === "AbortError" || err.name === "CanceledError") {
        console.log("Request cancelled - newer request in progress");
        return;
      }
      console.error("Error loading rents:", err);
      // No fallback needed - backend will provide mock data
      setRents([]);
      setTotalRecords(0);
    } finally {
      // Only update loading state if this request wasn't cancelled
      if (!abortController.signal.aborted) {
        setLoading(false);
        setIsInitialLoad(false);
      }
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm, sortColumn, sortDirection]);

  useEffect(() => {
    loadRents();

    // Cleanup: cancel any pending request when component unmounts
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadRents]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB"); // DD/MM/YYYY format
    } catch {
      return dateString;
    }
  };

  const getRentStatus = (statusCode) => {
    const statusMap = {
      0: 'in progress',
      1: 'active',
      2: 'failed',
      3: 'ended'
    };
    return statusMap[statusCode] || '';
  };

  const getStatusBadge = (statusCode) => {
    const statusText = getRentStatus(statusCode);
    const statusColors = {
      'in progress': "#ffc107",
      'active': "#28a745",
      'failed': "#dc3545",
      'ended': "#6c757d",
    };

    return (
      <span
        className="status-badge"
        style={{
          backgroundColor: statusColors[statusText] || "#6c757d",
          color: "white",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {statusText}
      </span>
    );
  };

  const getGuardianStatusBadge = (status) => {
    const statusColors = {
      Placed: "#28a745",
      "Documents Required": "#ffc107",
      NTQ: "#dc3545",
      "Payment Pending": "#fd7e14",
    };

    return (
      <span
        className="guardian-status-badge"
        style={{
          backgroundColor: statusColors[status] || "#6c757d",
          color: "white",
          padding: "2px 8px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        {status}
      </span>
    );
  };

  const handleActionClick = async (action, rent) => {
    switch (action) {
      case "edit-dates":
        navigate(`/rents/${rent.id}/dates/edit`);
        break;
      case "manage-officer":
        navigate(`/rents/${rent.id}/officer/edit`);
        break;
      case "payments":
        navigate(`/rents/${rent.id}/payments`);
        break;
      case "give-notice":
        navigate(`/rents/${rent.id}/notice-to-quit/create`);
        break;
      case "manage-ntq":
        navigate(`/rents/${rent.id}/notice-to-quit/edit`);
        break;
      case "change-rent":
        navigate(`/rents/${rent.room_id}/payments/edit`);
        break;
      case "finish":
        if (window.confirm("Are you sure you want to finish this rent?")) {
          try {
            await rentsService.finishRent(rent.id);
            toast.success("Rent finished successfully!");
            loadRents();
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to finish rent");
          }
        }
        break;
      case "fail":
        if (window.confirm("Are you sure you want to fail this rent?")) {
          try {
            await rentsService.failRent(rent.id);
            toast.success("Rent failed successfully!");
            loadRents();
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fail rent");
          }
        }
        break;
      case "reactivate":
        if (window.confirm("Are you sure you want to reactivate this rent?")) {
          try {
            await rentsService.reactivateRent(rent.id);
            toast.success("Rent reactivated successfully!");
            loadRents();
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to reactivate rent");
          }
        }
        break;
      case "restore":
        if (window.confirm("Are you sure you want to restore this rent?")) {
          try {
            await rentsService.restoreRent(rent.id);
            toast.success("Rent restored successfully!");
            loadRents();
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to restore rent");
          }
        }
        break;
      default:
        console.log("Unknown action:", action);
    }
  };

  const exportToCSV = () => {
    const headers = [
      "UGD",
      "Guardian",
      "Guardian Status",
      "Room",
      "Property",
      "Start Date",
      "End Date",
      "Guardian Officer",
      "Status",
    ];

    const csvContent = [
      headers.join(","),
      ...rents.map((rent) =>
        [
          rent.unique_guardian_number,
          rent.guardianName,
          rent.guardianStatus,
          rent.roomName,
          rent.propertyName,
          formatDate(rent.date_start),
          formatDate(rent.date_end),
          rent.guardianOfficerName,
          rent.status,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rents.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Rents...");

      if (!rents || rents.length === 0) {
        toast.error("No data available to export");
        return;
      }

      // Import jsPDF and autoTable
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF('landscape');

      // Add title
      doc.setFontSize(16);
      doc.text('Rents Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${totalRecords}`, 20, 40);

      if (debouncedSearchTerm) {
        doc.text(`Filtered by: "${debouncedSearchTerm}"`, 20, 50);
      }

      // Prepare table data based on visible columns
      const headers = [];
      const data = [];

      if (visibleColumns.ugd) headers.push('UGD');
      if (visibleColumns.guardian) headers.push('Guardian');
      if (visibleColumns.guardianStatus) headers.push('Guardian Status');
      if (visibleColumns.room) headers.push('Room');
      if (visibleColumns.property) headers.push('Property');
      if (visibleColumns.startDate) headers.push('Start Date');
      if (visibleColumns.endDate) headers.push('End Date');
      if (visibleColumns.guardianOfficer) headers.push('Guardian Officer');
      if (visibleColumns.status) headers.push('Status');

      rents.forEach((rent) => {
        const row = [];
        if (visibleColumns.ugd) row.push(String(rent.unique_guardian_number || ''));
        if (visibleColumns.guardian) row.push(String(rent.guardianName || ''));
        if (visibleColumns.guardianStatus) row.push(String(rent.guardianStatus || ''));
        if (visibleColumns.room) row.push(String(rent.roomName || ''));
        if (visibleColumns.property) row.push(String(rent.propertyName || ''));
        if (visibleColumns.startDate) row.push(String(formatDate(rent.date_start) || ''));
        if (visibleColumns.endDate) row.push(String(formatDate(rent.date_end) || ''));
        if (visibleColumns.guardianOfficer) row.push(String(rent.guardianOfficerName || ''));
        if (visibleColumns.status) row.push(String(getRentStatus(rent.status) || ''));
        data.push(row);
      });

      // Add table
      doc.autoTable({
        head: [headers],
        body: data,
        startY: searchTerm ? 60 : 50,
        styles: {
          fontSize: 8,
          cellPadding: 3,
          overflow: 'linebreak',
          halign: 'left',
        },
        headStyles: {
          fillColor: [66, 139, 202],
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245],
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
          doc.setFontSize(8);
          doc.text(`Page ${currentPage} of ${pageCount}`,
            doc.internal.pageSize.width - 30,
            doc.internal.pageSize.height - 10);
        }
      });

      const fileName = `rents_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      toast.success("PDF exported successfully!");

    } catch (error) {
      console.error("❌ [FRONTEND] Error generating PDF:", error);
      toast.error(`Error generating PDF: ${error.message}`);
    }
  };

  const printTable = () => {
    window.print();
  };

  // No client-side filtering/sorting/pagination needed - backend handles it
  // rents already contains the paginated, filtered, and sorted data from backend
  const paginatedRents = rents;

  // Only show full-screen loader on initial load, not during search/filter/pagination
  if (isInitialLoad && loading) return <Loader />;
  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <div
      className="container-fluid rents-laravel"
      style={{ backgroundColor: "white" }}
    >
      {/* Breadcrumb */}

      {/* <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          Dashboard
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Rents</span>
        </div>
      </div> */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black" }}
        >
          <a href="/dashboard">Dashboard</a>
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Rents</span>
        </div>
      </div>

      {/* Title */}
      <div className="row">
        <div className="col-md-12" style={{ margin: 0 }}>
          <div className="panel-title" style={{ margin: 0 }}>
            <h3 id="guardian-status-span">Rents</h3>
          </div>
        </div>
      </div>

      {/* DataTable Toolbar */}
      <div
        className="row"
        style={{ backgroundColor: "#F8F9FA", border: "none" }}
      >
        <div
          className="col-md-12"
          style={{ backgroundColor: "#F8F9FA", border: "none", margin: 0 }}
        >
          <div
            className="dt-toolbar"
            style={{ backgroundColor: "#F8F9FA", border: "none", margin: 0 }}
          >
            <div
              className="dt-toolbar-left"
              style={{ backgroundColor: "#F8F9FA", margin: 0 }}
            >
              <div
                className="form-group"
                style={{
                  backgroundColor: "#F8F9FA",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <label htmlFor="length-select">Show</label>
                <select
                  id="length-select"
                  className="form-control"
                  style={{ backgroundColor: "#F8F9FA" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={75}>75</option>
                  <option value={100}>100</option>
                </select>
                <label htmlFor="length-select">entries</label>
              </div>
            </div>

            <div className="dt-toolbar-right">
              <div
                className="form-group"
                style={{
                  backgroundColor: "#F8F9FA",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <label htmlFor="search-input">Search:</label>
                <input
                  ref={searchInputRef}
                  id="search-input"
                  type="search"
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page when searching
                    // Keep focus when typing
                    wasSearchFocusedRef.current = true;
                  }}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  placeholder="Search all columns..."
                  autoFocus={false}
                />
              </div>

              <div
                className="btn-group"
                style={{ backgroundColor: "#F8F9FA", border: "none" }}
              >
                <button
                  className="btn btn-default dropdown-toggle"
                  type="button"
                  onClick={() => setShowColumnVisibility(!showColumnVisibility)}
                >
                  Column Visibility
                </button>
                {showColumnVisibility && (
                  <div className="column-visibility-dropdown">
                    {Object.entries(visibleColumns).map(([key, visible]) => (
                      <label key={key} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={visible}
                          onChange={(e) =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [key]: e.target.checked,
                            }))
                          }
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div
                className="btn-group"
                style={{ display: "flex", flexWrap: "wrap" }}
              >
                <button className="btn btn-default" onClick={exportToCSV}>
                  Export as CSV
                </button>
                <button className="btn btn-default" onClick={exportToPDF}>
                  Export as PDF
                </button>
                <button className="btn btn-default" onClick={printTable}>
                  Print table
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <div className="row" style={{ margin: 0, border: "none" }}>
        <div className="col-md-12" style={{ margin: 0, border: "none" }}>
          <div
            className="panel-group"
            style={{ border: "none", background: "#F8F9FA", position: "relative" }}
          >
            {/* Loading overlay for search/filter operations */}
            {loading && !isInitialLoad && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
            )}
            {paginatedRents.length > 0 ? (
              <div className="dataTable_wrapper" style={{ border: "none" }}>
                <table
                  style={{ border: "none" }}
                  id="datatable-all-rents"
                  className="table table-hover dt-responsive"
                >
                  <thead>
                    <tr>
                      {visibleColumns.ugd && (
                        <th
                          onClick={() => {
                            const newColumn = "unique_guardian_number";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1); // Reset to first page when sorting
                          }}
                        >
                          UGD
                          {sortColumn === "unique_guardian_number" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.guardian && (
                        <th
                          onClick={() => {
                            const newColumn = "guardianName";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Guardian
                          {sortColumn === "guardianName" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.guardianStatus && (
                        <th
                          onClick={() => {
                            const newColumn = "guardianStatus";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Guardian status
                          {sortColumn === "guardianStatus" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.room && (
                        <th
                          onClick={() => {
                            const newColumn = "roomName";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Room
                          {sortColumn === "roomName" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.property && (
                        <th
                          onClick={() => {
                            const newColumn = "propertyName";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Property
                          {sortColumn === "propertyName" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.startDate && (
                        <th
                          onClick={() => {
                            const newColumn = "date_start";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Start date
                          {sortColumn === "date_start" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.endDate && (
                        <th
                          onClick={() => {
                            const newColumn = "date_end";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          End date
                          {sortColumn === "date_end" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.guardianOfficer && (
                        <th
                          onClick={() => {
                            const newColumn = "guardianOfficerName";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Guardian Officer
                          {sortColumn === "guardianOfficerName" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.status && (
                        <th
                          onClick={() => {
                            const newColumn = "status";
                            if (sortColumn === newColumn) {
                              setSortDirection(
                                sortDirection === "asc" ? "desc" : "asc"
                              );
                            } else {
                              setSortColumn(newColumn);
                              setSortDirection("asc");
                            }
                            setCurrentPage(1);
                          }}
                        >
                          Status
                          {sortColumn === "status" && (
                            <span className={`sort-icon ${sortDirection}`}>
                              {sortDirection === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </th>
                      )}
                      {visibleColumns.actions && (
                        <th className="action-head">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRents.map((rent) => (
                      <tr key={rent.id}>
                        {visibleColumns.ugd && (
                          <td>{rent.unique_guardian_number}</td>
                        )}
                        {visibleColumns.guardian && (
                          <td>{rent.guardianName}</td>
                        )}
                        {visibleColumns.guardianStatus && (
                          <td>{getGuardianStatusBadge(rent.guardianStatus)}</td>
                        )}
                        {visibleColumns.room && <td>{rent.roomName}</td>}
                        {visibleColumns.property && (
                          <td>{rent.propertyName}</td>
                        )}
                        {visibleColumns.startDate && (
                          <td>{formatDate(rent.date_start)}</td>
                        )}
                        {visibleColumns.endDate && (
                          <td>{formatDate(rent.date_end)}</td>
                        )}
                        {visibleColumns.guardianOfficer && (
                          <td>{rent.guardianOfficerName}</td>
                        )}
                        {visibleColumns.status && (
                          <td>{getStatusBadge(rent.status)}</td>
                        )}
                        {visibleColumns.actions && (
                          <td className="action">
                            <div
                              className="action-buttons"
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                gap: "5px",
                                flexWrap: "wrap",
                              }}
                            >
                              {/* Laravel buttons_index.blade.php - Exact Match */}
                              
                              {/* 1. Edit dates - Laravel: @if(in_array('rents.dates.edit', $globalPermissions)) */}
                              {hasPermission("rents.dates.edit") && (
                                <button
                                  className="btn btn-sm btn-primary btn-action"
                                  onClick={() =>
                                    handleActionClick("edit-dates", rent)
                                  }
                                  title="Edit dates"
                                >
                                  Edit dates
                                </button>
                              )}

                              {/* 2. Manage Officer - Laravel: @if(in_array('rents.edit', $globalPermissions)) */}
                              {hasPermission("rents.edit") && (
                                <button
                                  className="btn btn-sm btn-info btn-action"
                                  onClick={() =>
                                    handleActionClick("manage-officer", rent)
                                  }
                                  title="Manage Officer"
                                >
                                  Manage Officer
                                </button>
                              )}

                              {/* 3. Payments - Laravel: @if (in_array('payments.list', $globalPermissions)) */}
                              {hasPermission("payments.list") && (
                                <button
                                  className="btn btn-sm btn-primary btn-action"
                                  onClick={() =>
                                    handleActionClick("payments", rent)
                                  }
                                  title="Payments"
                                >
                                  Payments
                                </button>
                              )}

                              {/* 4. Give notice - Laravel: @if(in_array('rents.notice_to_quit.create', $globalPermissions) && !$model->notice_to_quit && $model->isActive()) */}
                              {hasPermission("rents.notice_to_quit.create") &&
                                !rent.notice_to_quit_id &&
                                rent.status === 1 && (
                                  <button
                                    className="btn btn-sm btn-primary btn-action"
                                    onClick={() =>
                                      handleActionClick("give-notice", rent)
                                    }
                                    title="Give notice"
                                  >
                                    Give notice
                                  </button>
                                )}

                              {/* 5. Manage NTQ - Laravel: @if(in_array('rents.notice_to_quit.manage', $globalPermissions) && $model->notice_to_quit && $model->isActive()) */}
                              {hasPermission("rents.notice_to_quit.manage") &&
                                rent.notice_to_quit_id &&
                                rent.status === 1 && (
                                  <button
                                    className="btn btn-sm btn-primary btn-action"
                                    onClick={() =>
                                      handleActionClick("manage-ntq", rent)
                                    }
                                    title="Manage NTQ"
                                  >
                                    Manage NTQ
                                  </button>
                                )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center">
                <p>No rents!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="row">
        <div className="col-md-12">
          <div className="pagination-info">
            <div className="pagination-left">
              Showing {totalRecords > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{" "}
              {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
              {totalRecords.toLocaleString()} entries
            </div>
            <div className="pagination-right">
              <nav>
                <ul className="pagination">
                  <li
                    className={`page-item ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from(
                    { length: Math.ceil(totalRecords / itemsPerPage) },
                    (_, i) => i + 1
                  )
                    .filter(
                      (page) =>
                        page === 1 ||
                        page === Math.ceil(totalRecords / itemsPerPage) ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <li className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        )}
                        <li
                          className={`page-item ${
                            page === currentPage ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      </React.Fragment>
                    ))}
                  <li
                    className={`page-item ${
                      currentPage === Math.ceil(totalRecords / itemsPerPage)
                        ? "disabled"
                        : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={
                        currentPage === Math.ceil(totalRecords / itemsPerPage)
                      }
                    >
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentsLaravel;
